/**
 * PUT a file to a (pre-signed) storage URL with upload-progress reporting.
 *
 * `fetch` cannot emit request-body progress, so a determinate upload bar is impossible with it.
 * `XMLHttpRequest.upload.onprogress` can, which is the whole reason this helper exists. It is a
 * behavioral drop-in for the bare `fetch` PUT it replaces: same method, the single `Content-Type`
 * header (sending our API auth headers would leak the Toolbox token to storage AND invalidate the
 * presigned signature), and any non-2xx status throws the same `Storage upload failed (<status>)`
 * error so the callers' multipart fallback still triggers.
 */
export type UploadProgress = {
  loaded: number;
  total: number;
  /** 0–100, integer. 0 while the size is not yet computable. */
  percent: number;
};

export type UploadProgressCallback = (progress: UploadProgress) => void;

export function putFileToStorageWithProgress(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: UploadProgressCallback,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', contentType);

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (!event.lengthComputable) return;
        const percent = event.total
          ? Math.min(100, Math.round((event.loaded / event.total) * 100))
          : 0;
        onProgress({ loaded: event.loaded, total: event.total, percent });
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Guarantee a terminal 100% even if the last progress event never arrived.
        onProgress?.({ loaded: file.size, total: file.size, percent: 100 });
        resolve();
        return;
      }
      reject(new Error(`Storage upload failed (${xhr.status}).`));
    };

    xhr.onerror = () => reject(new Error('Storage upload failed (network error).'));
    xhr.onabort = () => reject(new Error('Storage upload was cancelled.'));

    xhr.send(file);
  });
}
